// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  formatDateTime,
} from 'common.helpers/dist/browser';
import {
  defaultGetData,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

type $DataItem = $Model<'Crewmember'> & {
  CrewmemberRegattaNotification: $Model<'CrewmemberRegattaNotification'>;
};

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$DataItem>;
  loading: boolean;
  mainItem: $Model<'RegattaNotification'> | null;
};

const pageId = adminPages.adminRegattaNotificationCrewmembersPage;

class AdminRegattaNotificationCrewmembersPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      mainItem: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Crewmember') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      RegattaNotificationId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (RegattaNotificationId) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneAdminRegattaNotification({
            id: RegattaNotificationId,
          });
          const data = await Services.Api.getManyAdminRegattaNotificationCrewmember({
            RegattaNotificationId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeaderDescription(): string {
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      return `${mainItem.Type} ${formatDateTime({
        dateFormat: 'YYYY-MM-DD',
        timeFormat: 'HH:mm',
        value: mainItem.createdAt,
      })}`;
    }

    return '';
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          description={this.renderHeaderDescription()}
          title={t('RegattaNotifications')}
          titleButton={(
            <Button
              className={'mr-2'}
              color={'secondary'}
              id={'redirect-admin-notifications-page-button'}
              onClick={() => redirect({
                pathname: '/admin/notifications',
              })}
              size={'sm'}
            >
              <em className={'fa icon-arrow-left'} />
            </Button>
          )}
        />
      </Card>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Email')}</th>
            <th>{t('Sent')}</th>
            <th>{t('Read')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $DataItem) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Email}</td>
              <TdDate value={dataItem.CrewmemberRegattaNotification.Sent} />
              <TdDate value={dataItem.CrewmemberRegattaNotification.Read} />
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminRegattaNotificationCrewmembersPageView;
